import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { FileText, MapPin, Users, Baby, Calendar, Plane, CreditCard, Plus, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FormularioModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    whatsapp: '',
    cidadeSaida: '',
    tipoDestino: '',
    destino: '',
    mesViagem: '',
    dataViagem: '',
    dataLivre: '',
    quantidadeAdultos: 1,
    quantidadeCriancas: 0,
    idadesCriancas: [],
    formaPagamento: '',
    observacoes: ''
  });

  const datasPortoSeguro = {
    'MARÇO': ['21 a 27/03'],
    'ABRIL': ['11 a 18/04', '20 a 27/04'],
    'MAIO': ['09 a 16/05', '20 a 27/05'],
    'JUNHO': ['06 a 13/06', '20 a 27/06'],
    'JULHO': ['01 a 08/07', '07 a 14/07', '15 a 21/07', '23 a 30/07'],
    'AGOSTO': ['10 a 17/08', '20 a 27/08'],
    'SETEMBRO': ['07 a 14/09', '20 a 27/09'],
    'OUTUBRO': ['11 a 18/10', '20 a 27/10'],
    'NOVEMBRO': ['08 a 15/11', '22 a 29/11'],
    'DEZEMBRO': ['01 a 08/12', '07 a 14/12', '19 a 26/12']
  };

  const meses = Object.keys(datasPortoSeguro);

  const formasPagamento = [
    { id: 'cartao', label: 'Cartão de Crédito', icon: '💳' },
    { id: 'boleto', label: 'Boleto', icon: '📄' },
    { id: 'cartao_boleto', label: 'Cartão de Crédito e Boleto', icon: '💳📄' },
    { id: 'pix', label: 'Pix/À Vista', icon: '💰' }
  ];

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTipoDestinoChange = (tipo) => {
    setFormData(prev => ({
      ...prev, tipoDestino: tipo,
      destino: tipo === 'Porto Seguro - BA' ? 'Porto Seguro - BA' : '',
      mesViagem: '', dataViagem: '', dataLivre: ''
    }));
  };

  const handleMesChange = (mes) => {
    setFormData(prev => ({ ...prev, mesViagem: mes, dataViagem: '' }));
  };

  // Stepper functions for adults
  const incrementAdultos = () => {
    setFormData(prev => ({ ...prev, quantidadeAdultos: Math.min(20, prev.quantidadeAdultos + 1) }));
  };

  const decrementAdultos = () => {
    setFormData(prev => ({ ...prev, quantidadeAdultos: Math.max(1, prev.quantidadeAdultos - 1) }));
  };

  const handleAdultosChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setFormData(prev => ({ ...prev, quantidadeAdultos: Math.max(1, Math.min(20, value)) }));
  };

  // Stepper functions for children
  const incrementCriancas = () => {
    const newValue = Math.min(10, formData.quantidadeCriancas + 1);
    const newIdades = [...formData.idadesCriancas];
    while (newIdades.length < newValue) newIdades.push('');
    setFormData(prev => ({ ...prev, quantidadeCriancas: newValue, idadesCriancas: newIdades }));
  };

  const decrementCriancas = () => {
    const newValue = Math.max(0, formData.quantidadeCriancas - 1);
    const newIdades = formData.idadesCriancas.slice(0, newValue);
    setFormData(prev => ({ ...prev, quantidadeCriancas: newValue, idadesCriancas: newIdades }));
  };

  const handleCriancasChange = (e) => {
    const value = Math.max(0, Math.min(10, parseInt(e.target.value) || 0));
    const newIdades = [...formData.idadesCriancas];
    while (newIdades.length < value) newIdades.push('');
    while (newIdades.length > value) newIdades.pop();
    setFormData(prev => ({ ...prev, quantidadeCriancas: value, idadesCriancas: newIdades }));
  };

  const handleIdadeCriancaChange = (index, idade) => {
    const newIdades = [...formData.idadesCriancas];
    newIdades[index] = idade;
    setFormData(prev => ({ ...prev, idadesCriancas: newIdades }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const dataFinal = formData.tipoDestino === 'Porto Seguro - BA' ? formData.dataViagem : formData.dataLivre;

    try {
      const payload = {
        nome: formData.nome, whatsapp: formData.whatsapp, cidadeSaida: formData.cidadeSaida,
        destino: formData.tipoDestino === 'Porto Seguro - BA' ? 'Porto Seguro - BA' : formData.destino,
        dataViagem: dataFinal, mesViagem: formData.mesViagem,
        quantidadeAdultos: formData.quantidadeAdultos, quantidadeCriancas: formData.quantidadeCriancas,
        idadesCriancas: formData.idadesCriancas.filter(i => i !== ''),
        formaPagamento: formasPagamento.find(f => f.id === formData.formaPagamento)?.label || formData.formaPagamento,
        observacoes: formData.observacoes, timestamp: new Date().toISOString(), origem: 'Site A.R. Turismo - Modal'
      };

      await fetch('https://n8n.andrewmendes.com.br/webhook/siteagencia', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });

      sessionStorage.setItem('formData', JSON.stringify(payload));
      navigate('/obrigado');
      onClose();
    } catch (error) {
      alert('Erro ao enviar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const isPortoSeguro = formData.tipoDestino === 'Porto Seguro - BA';
  const isOutroDestino = formData.tipoDestino === 'Outro Destino';
  const canSubmit = formData.formaPagamento && ((isPortoSeguro && formData.dataViagem) || (isOutroDestino && formData.destino));

  // Number Stepper Component
  const NumberStepper = ({ value, onChange, onIncrement, onDecrement, min = 0, max = 20, label }) => (
    <div className="flex items-center justify-between bg-gray-50 rounded-lg border border-gray-200 p-1">
      <button
        type="button"
        onClick={onDecrement}
        disabled={value <= min}
        className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
      >
        <Minus className="w-5 h-5" />
      </button>
      <input
        type="number"
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        className="w-16 h-10 text-center text-lg font-bold bg-transparent border-none focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        inputMode="numeric"
      />
      <button
        type="button"
        onClick={onIncrement}
        disabled={value >= max}
        className="w-10 h-10 flex items-center justify-center rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95"
      >
        <Plus className="w-5 h-5" />
      </button>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto p-4 sm:p-6" 
        data-testid="form-modal"
        style={{ paddingBottom: '2rem' }}
      >
        <DialogHeader className="pb-2 sticky top-0 bg-white z-10">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-gray-900">
            Solicite seu <span className="text-red-600">Orçamento</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 pb-4">
          <div className="space-y-1">
            <Label htmlFor="nome" className="text-sm">Nome Completo *</Label>
            <Input 
              id="nome" 
              name="nome" 
              required 
              data-testid="form-nome-input" 
              value={formData.nome} 
              onChange={handleChange} 
              placeholder="Seu nome" 
              className="h-12 text-base"
              autoComplete="name"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="whatsapp" className="text-sm">WhatsApp *</Label>
            <Input 
              id="whatsapp" 
              name="whatsapp" 
              type="tel" 
              required 
              data-testid="form-whatsapp-input" 
              value={formData.whatsapp} 
              onChange={handleChange} 
              placeholder="(61) 99999-9999" 
              className="h-12 text-base"
              inputMode="tel"
              autoComplete="tel"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="cidadeSaida" className="flex items-center gap-1 text-sm">
              <MapPin className="w-3 h-3 text-red-500" />Cidade de Saída *
            </Label>
            <Input 
              id="cidadeSaida" 
              name="cidadeSaida" 
              required 
              data-testid="form-cidade-input" 
              value={formData.cidadeSaida} 
              onChange={handleChange} 
              placeholder="Ex: Brasília, Goiânia, Anápolis..." 
              className="h-12 text-base"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-1 text-sm">
              <Plane className="w-3 h-3 text-cyan-500" />Destino da Viagem *
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => handleTipoDestinoChange('Porto Seguro - BA')}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${isPortoSeguro ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}>
                🏖️ Porto Seguro - BA
              </button>
              <button type="button" onClick={() => handleTipoDestinoChange('Outro Destino')}
                className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${isOutroDestino ? 'border-cyan-500 bg-cyan-50 text-cyan-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}>
                ✈️ Outro Destino
              </button>
            </div>
          </div>

          {isPortoSeguro && (
            <div className="space-y-2 p-3 bg-red-50 rounded-lg border border-red-200">
              <Label className="flex items-center gap-1 text-sm text-red-700 font-semibold">
                <Calendar className="w-3 h-3" />Escolha o mês da viagem *
              </Label>
              <div className="grid grid-cols-3 gap-1">
                {meses.map((mes) => (
                  <button key={mes} type="button" onClick={() => handleMesChange(mes)}
                    className={`p-2 rounded text-xs font-medium transition-all ${formData.mesViagem === mes ? 'bg-red-600 text-white' : 'bg-white border border-red-200 text-red-700 hover:bg-red-100'}`}>
                    {mes}
                  </button>
                ))}
              </div>
              {formData.mesViagem && (
                <div className="mt-3 space-y-2">
                  <Label className="text-sm text-red-700 font-semibold">Escolha a data *</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {datasPortoSeguro[formData.mesViagem].map((data) => (
                      <button key={data} type="button" onClick={() => setFormData(prev => ({ ...prev, dataViagem: data }))}
                        className={`p-2 rounded text-sm font-medium transition-all ${formData.dataViagem === data ? 'bg-green-600 text-white' : 'bg-white border border-red-200 text-gray-700 hover:bg-red-100'}`}>
                        {data}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {isOutroDestino && (
            <div className="space-y-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
              <div className="space-y-1">
                <Label htmlFor="destino" className="text-sm text-cyan-700 font-semibold">Qual o destino? *</Label>
                <Input 
                  id="destino" 
                  name="destino" 
                  required={isOutroDestino} 
                  value={formData.destino} 
                  onChange={handleChange} 
                  placeholder="Ex: Rio de Janeiro, Maceió..." 
                  className="h-12 text-base" 
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="dataLivre" className="text-sm text-cyan-700 font-semibold">Quando pretende viajar?</Label>
                <Input 
                  id="dataLivre" 
                  name="dataLivre" 
                  value={formData.dataLivre} 
                  onChange={handleChange} 
                  placeholder="Ex: Julho de 2025, Férias..." 
                  className="h-12 text-base" 
                />
              </div>
            </div>
          )}

          {/* Quantidade de Pessoas com Stepper */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm">
                <Users className="w-3 h-3 text-blue-500" />Adultos *
              </Label>
              <NumberStepper
                value={formData.quantidadeAdultos}
                onChange={handleAdultosChange}
                onIncrement={incrementAdultos}
                onDecrement={decrementAdultos}
                min={1}
                max={20}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm">
                <Baby className="w-3 h-3 text-pink-500" />Crianças (0-12)
              </Label>
              <NumberStepper
                value={formData.quantidadeCriancas}
                onChange={handleCriancasChange}
                onIncrement={incrementCriancas}
                onDecrement={decrementCriancas}
                min={0}
                max={10}
              />
            </div>
          </div>

          {formData.quantidadeCriancas > 0 && (
            <div className="space-y-2 p-2 bg-pink-50 rounded-lg border border-pink-200">
              <Label className="text-pink-700 font-semibold text-sm">Idade das crianças:</Label>
              <div className="grid grid-cols-3 gap-2">
                {formData.idadesCriancas.map((idade, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <span className="text-xs text-gray-600">{index + 1}ª:</span>
                    <select 
                      value={idade} 
                      onChange={(e) => handleIdadeCriancaChange(index, e.target.value)} 
                      className="flex-1 h-10 px-2 rounded-md border border-pink-300 bg-white text-base"
                      data-testid={`form-idade-crianca-${index}`}
                    >
                      <option value="">?</option>
                      {[...Array(13)].map((_, i) => (<option key={i} value={i}>{i}</option>))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="flex items-center gap-1 text-sm">
              <CreditCard className="w-3 h-3 text-green-600" />Forma de Pagamento *
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {formasPagamento.map((forma) => (
                <button key={forma.id} type="button" onClick={() => setFormData(prev => ({ ...prev, formaPagamento: forma.id }))}
                  className={`p-3 rounded-lg border-2 text-xs font-medium transition-all ${formData.formaPagamento === forma.id ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}>
                  <span className="mr-1">{forma.icon}</span> {forma.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="observacoes" className="text-sm">Observações (opcional)</Label>
            <Textarea 
              id="observacoes" 
              name="observacoes" 
              data-testid="form-observacoes-input" 
              value={formData.observacoes} 
              onChange={handleChange} 
              className="min-h-[60px] text-base" 
            />
          </div>

          <div className="pt-2">
            <Button 
              type="submit" 
              disabled={loading || !canSubmit} 
              data-testid="form-submit-button" 
              className="w-full bg-red-600 hover:bg-red-700 text-white py-6 rounded-full flex flex-col items-center justify-center gap-0 disabled:opacity-50"
            >
              {loading ? 'Enviando...' : (
                <>
                  <span className="flex items-center gap-2 text-base font-bold">
                    <FileText className="h-4 w-4" />Fechar Contrato
                  </span>
                  <span className="text-xs font-normal opacity-70">Seguir para Fechamento do Contrato</span>
                </>
              )}
            </Button>
          </div>
        </form>
        
        <p className="text-xs text-center text-gray-500">🔒 Seus dados estão seguros</p>
      </DialogContent>
    </Dialog>
  );
};

export default FormularioModal;
